// SVG export utilities for WordCloud

// Helper function to ensure fonts are loaded
const ensureFontsLoaded = async (
  fontFamily: string = 'pretendard',
): Promise<void> => {
  if (!fontFamily || !document.fonts) return;

  try {
    // Wait for fonts to load
    await document.fonts.ready;

    // Check if specific font is available
    if (!document.fonts.check(`12px "${fontFamily}"`)) {
      console.warn(`Font "${fontFamily}" may not be available`);
    }
  } catch (error) {
    console.warn('Font loading check failed:', error);
  }
};

// Helper function to inject font styles into SVG
const injectFontStyles = (
  svgData: string,
  fontFamily: string = 'pretendard',
): string => {
  // Create font style to inject
  const fontStyle = `
    <defs>
      <style type="text/css">
        <![CDATA[
          text {
            font-family: "${fontFamily}", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important;
          }
        ]]>
      </style>
    </defs>
  `;

  // Inject style after opening svg tag
  return svgData.replace('<svg', `<svg`).replace('>', `>${fontStyle}`);
};

export const downloadSVG = (
  svgElement: SVGElement,
  filename: string = 'wordcloud.svg',
) => {
  const svgData = new XMLSerializer().serializeToString(svgElement);
  const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
  const svgUrl = URL.createObjectURL(svgBlob);

  const downloadLink = document.createElement('a');
  downloadLink.href = svgUrl;
  downloadLink.download = filename;
  document.body.appendChild(downloadLink);
  downloadLink.click();
  document.body.removeChild(downloadLink);
  URL.revokeObjectURL(svgUrl);
};

export const downloadPNG = async (
  svgElement: SVGElement,
  filename: string = 'wordcloud.png',
  options?: {
    width?: number;
    height?: number;
    scale?: number;
    fontFamily?: string;
    backgroundColor?: string;
  },
) => {
  try {
    // Ensure fonts are loaded before conversion
    await ensureFontsLoaded(options?.fontFamily);

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Cannot get canvas context');

    const clonedSvgElement = svgElement.cloneNode(true) as SVGElement;

    let svgData = new XMLSerializer().serializeToString(clonedSvgElement);

    // Inject font styles if specified
    svgData = injectFontStyles(svgData, options?.fontFamily);

    const img = new Image();

    return new Promise<void>((resolve, reject) => {
      img.onload = () => {
        // Use custom dimensions if provided, otherwise use original dimensions
        let targetWidth = options?.width || img.width;
        let targetHeight = options?.height || img.height;

        // Apply scale factor if provided
        if (options?.scale) {
          targetWidth = (options.width || img.width) * options.scale;
          targetHeight = (options.height || img.height) * options.scale;
        }

        canvas.width = targetWidth;
        canvas.height = targetHeight;

        // Set background color
        ctx.fillStyle = options?.backgroundColor || 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Scale the image to fit the canvas
        ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

        canvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error('Failed to create blob'));
            return;
          }

          const url = URL.createObjectURL(blob);
          const downloadLink = document.createElement('a');
          downloadLink.href = url;
          downloadLink.download = filename;
          document.body.appendChild(downloadLink);
          downloadLink.click();
          document.body.removeChild(downloadLink);
          URL.revokeObjectURL(url);
          resolve();
        }, 'image/png');
      };

      img.onerror = () => reject(new Error('Failed to load SVG'));

      // Add a small delay to ensure fonts are rendered
      setTimeout(() => {
        img.src =
          'data:image/svg+xml;base64,' +
          btoa(unescape(encodeURIComponent(svgData)));
      }, 100);
    });
  } catch (error) {
    console.error('Error downloading PNG:', error);
    throw error;
  }
};

export const copySVGToClipboard = async (
  svgElement: SVGElement,
  options?: {
    width?: number;
    height?: number;
    scale?: number;
    fontFamily?: string;
    backgroundColor?: string;
  },
) => {
  try {
    // Ensure fonts are loaded before conversion
    await ensureFontsLoaded(options?.fontFamily);

    const clonedSvgElement = svgElement.cloneNode(true) as SVGElement;

    let svgData = new XMLSerializer().serializeToString(clonedSvgElement);

    // Inject font styles if specified
    svgData = injectFontStyles(svgData, options?.fontFamily);

    if (navigator.clipboard && navigator.clipboard.write) {
      // Modern clipboard API - copy as image
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Cannot get canvas context');

      const img = new Image();
      return new Promise<void>((resolve, reject) => {
        img.onload = async () => {
          // Use custom dimensions if provided, otherwise use original dimensions
          let targetWidth = options?.width || img.width;
          let targetHeight = options?.height || img.height;

          // Apply scale factor if provided
          if (options?.scale) {
            targetWidth = (options.width || img.width) * options.scale;
            targetHeight = (options.height || img.height) * options.scale;
          }

          canvas.width = targetWidth;
          canvas.height = targetHeight;

          // Set background color
          ctx.fillStyle = options?.backgroundColor || 'white';
          ctx.fillRect(0, 0, canvas.width, canvas.height);

          // Scale the image to fit the canvas
          ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

          canvas.toBlob(async (blob) => {
            if (!blob) {
              reject(new Error('Failed to create blob'));
              return;
            }

            try {
              await navigator.clipboard.write([
                new ClipboardItem({ 'image/png': blob }),
              ]);
              resolve();
            } catch (clipboardError) {
              // Fallback to text
              await navigator.clipboard.writeText(svgData);
              resolve();
            }
          }, 'image/png');
        };

        img.onerror = () => {
          // Fallback to text
          navigator.clipboard.writeText(svgData).then(resolve).catch(reject);
        };

        // Add a small delay to ensure fonts are rendered
        setTimeout(() => {
          img.src =
            'data:image/svg+xml;base64,' +
            btoa(unescape(encodeURIComponent(svgData)));
        }, 100);
      });
    } else {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = svgData;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  } catch (error) {
    console.error('Error copying to clipboard:', error);
    throw error;
  }
};

export const getSVGElement = (
  containerElement: HTMLElement,
): SVGElement | null => {
  return containerElement.querySelector('svg');
};
